import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';

const SpecialFieldWrapper = ({ icon, title, label, color, required, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        p: 2,
        mb: 1,
        backgroundColor: theme.palette.background.paper,
        ...(required && {
          borderLeft: '3px solid #FF5630',
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: children ? 1.5 : 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}14`,
          }}
        >
          <Iconify icon={icon} sx={{ width: 18, height: 18, color }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: theme.palette.text.primary }}>
              {title}
            </Typography>
            {required && (
              <Typography component="span" sx={{ color: '#FF5630', fontSize: '0.875rem', fontWeight: 700 }}>
                *
              </Typography>
            )}
          </Box>
          {label && (
            <Typography sx={{ fontSize: '0.7rem', color: theme.palette.text.secondary }}>
              {label}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Box>
  );
};

SpecialFieldWrapper.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string,
  color: PropTypes.string.isRequired,
  required: PropTypes.bool,
  children: PropTypes.node,
};

SpecialFieldWrapper.defaultProps = {
  label: '',
  required: false,
  children: null,
};

// --- Mock Components ---

export const CaptureContactDetails = ({ label, required }) => (
  <SpecialFieldWrapper
    icon="solar:user-id-bold"
    title="Capture Contact Details"
    label={label}
    color="#006C9C"
    required={required}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.6 }}>
      <Box sx={{ height: 36, borderRadius: '8px', border: '1px dashed #919EAB', display: 'flex', alignItems: 'center', px: 1.5 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#919EAB' }}>First Name</Typography>
      </Box>
      <Box sx={{ height: 36, borderRadius: '8px', border: '1px dashed #919EAB', display: 'flex', alignItems: 'center', px: 1.5 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#919EAB' }}>Last Name</Typography>
      </Box>
      <Box sx={{ height: 36, borderRadius: '8px', border: '1px dashed #919EAB', display: 'flex', alignItems: 'center', px: 1.5 }}>
        <Typography sx={{ fontSize: '0.75rem', color: '#919EAB' }}>Phone / Email</Typography>
      </Box>
    </Box>
  </SpecialFieldWrapper>
);

CaptureContactDetails.propTypes = { label: PropTypes.string, required: PropTypes.bool };
CaptureContactDetails.defaultProps = { label: '', required: false };

// eslint-disable-next-line no-unused-vars
export const ConfirmSpeaker = ({ label }) => {
  const PREVIEW_OPTIONS = [
    { title: 'Yes, speaking', subtitle: 'Correct person', icon: 'solar:check-circle-bold', color: '#22C55E', bgColor: '#22C55E14' },
    { title: 'No answer', subtitle: 'Voicemail', icon: 'solar:phone-calling-rounded-bold', color: '#FFAB00', bgColor: '#FFAB0014' },
    { title: 'Wrong person', subtitle: '', icon: 'solar:user-cross-bold', color: '#FF5630', bgColor: '#FF563014' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Response options */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {PREVIEW_OPTIONS.map((option) => (
          <Box
            key={option.title}
            sx={{
              flex: '1 1 auto',
              minWidth: 140,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: '12px',
              border: '1.5px solid rgba(145, 158, 171, 0.32)',
              backgroundColor: '#fff',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: option.bgColor,
                flexShrink: 0,
              }}
            >
              <Iconify icon={option.icon} sx={{ width: 20, height: 20, color: option.color }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#212B36', lineHeight: 1.3 }}>
                {option.title}
              </Typography>
              {option.subtitle && (
                <Typography sx={{ fontSize: '0.7rem', color: '#919EAB', lineHeight: 1.3 }}>
                  {option.subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

ConfirmSpeaker.propTypes = { label: PropTypes.string };
ConfirmSpeaker.defaultProps = { label: '' };

export const AnswerTheCall = ({ label, required }) => (
  <SpecialFieldWrapper
    icon="solar:phone-calling-bold"
    title="Answer the Call"
    label={label}
    color="#00B8D9"
    required={required}
  >
    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', opacity: 0.6 }}>
      Completed by selector appears in card header
    </Typography>
  </SpecialFieldWrapper>
);

AnswerTheCall.propTypes = { label: PropTypes.string, required: PropTypes.bool };
AnswerTheCall.defaultProps = { label: '', required: false };

// eslint-disable-next-line no-unused-vars
export const IdentifyCaller = ({ label, required }) => {
  const PREVIEW_OPTIONS = [
    { title: 'New Patient', subtitle: 'First time caller', icon: 'solar:user-plus-bold', color: '#00A76F', bgColor: '#00A76F14' },
    { title: 'Existing Patient', subtitle: 'Already in system', icon: 'solar:user-check-bold', color: '#006C9C', bgColor: '#006C9C14' },
  ];

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Caller Type Options */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {PREVIEW_OPTIONS.map((option) => (
          <Box
            key={option.title}
            sx={{
              flex: '1 1 auto',
              minWidth: 140,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: '12px',
              border: '1.5px solid rgba(145, 158, 171, 0.32)',
              backgroundColor: '#fff',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: option.bgColor,
                flexShrink: 0,
              }}
            >
              <Iconify icon={option.icon} sx={{ width: 20, height: 20, color: option.color }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#212B36', lineHeight: 1.3 }}>
                {option.title}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#919EAB', lineHeight: 1.3 }}>
                {option.subtitle}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Name Fields Preview */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            <Iconify icon="solar:user-bold" sx={{ width: 14, height: 14, color: '#637381' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#637381' }}>
              First Name
            </Typography>
            {required && (
              <Typography component="span" sx={{ color: '#FF5630', fontSize: '0.875rem', fontWeight: 700 }}>
                *
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              height: 42,
              borderRadius: '10px',
              border: '1px solid rgba(145, 158, 171, 0.32)',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', color: '#919EAB' }}>Enter first name...</Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
            <Iconify icon="solar:user-bold" sx={{ width: 14, height: 14, color: '#637381' }} />
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#637381' }}>
              Last Name
            </Typography>
          </Box>
          <Box
            sx={{
              height: 42,
              borderRadius: '10px',
              border: '1px solid rgba(145, 158, 171, 0.32)',
              backgroundColor: '#fff',
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', color: '#919EAB' }}>Enter last name...</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

IdentifyCaller.propTypes = { label: PropTypes.string, required: PropTypes.bool };
IdentifyCaller.defaultProps = { label: '', required: false };

// eslint-disable-next-line no-unused-vars
export const ReasonForCalling = ({ label }) => (
  <Box sx={{ width: '100%' }}>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
      {/* Reason for call */}
      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Iconify icon="solar:chat-round-call-bold" sx={{ width: 14, height: 14, color: '#7635DC' }} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#637381' }}>
            Reason for call
          </Typography>
        </Box>
        <Box
          sx={{
            height: 42,
            borderRadius: '10px',
            border: '1px solid rgba(145, 158, 171, 0.32)',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#919EAB' }}>Select reason...</Typography>
          <Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20, color: '#919EAB' }} />
        </Box>
      </Box>

      {/* Treatment interest */}
      <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <Iconify icon="mdi:tooth" sx={{ width: 14, height: 14, color: '#00A76F' }} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#637381' }}>
            Treatment interest
          </Typography>
        </Box>
        <Box
          sx={{
            height: 42,
            borderRadius: '10px',
            border: '1px solid rgba(145, 158, 171, 0.32)',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#919EAB' }}>Select treatment...</Typography>
          <Iconify icon="eva:chevron-down-fill" sx={{ width: 20, height: 20, color: '#919EAB' }} />
        </Box>
      </Box>
    </Box>
  </Box>
);

ReasonForCalling.propTypes = { label: PropTypes.string };
ReasonForCalling.defaultProps = { label: '' };

const DEFAULT_FOLLOWUP_TEXT = 'Ensure WARM and COLD leads are tagged for proactive follow-up within specific timeframes (24 hours, 1 week, etc.) with email/SMS nudges linking testimonials or offers. Enter on CRM.';

// eslint-disable-next-line no-unused-vars
export const CloseCall = ({ label, required, metadata }) => {
  const INTEREST_OPTIONS = [
    { title: 'HOT', subtitle: 'Highly interested, likely to book', emoji: '🔥', bgColor: '#22C55E14' },
    { title: 'WARM', subtitle: 'Some interest, may need persuasion', emoji: '☀️', bgColor: '#FFAB0014' },
    { title: 'COLD', subtitle: 'Not ready or not serious', emoji: '❄️', bgColor: '#00B8D914' },
  ];

  const OUTCOME_OPTIONS = [
    { title: 'Submit', icon: 'mdi:send', color: '#637381' },
    { title: 'Appointment Not Booked', icon: 'mdi:calendar-remove', color: '#FF5630' },
    { title: 'Book An Appointment', icon: 'mdi:check-circle', color: '#22C55E' },
  ];

  // Get followUp text from metadata or use default
  const followUpText = metadata?.hints?.followUp || DEFAULT_FOLLOWUP_TEXT;

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Interest Level Section */}
      <Box>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#212B36', mb: 0.5 }}>
          Assessing Interest Level
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#637381', mb: 2 }}>
          Internal tracking for team
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: 0.7 }}>
          {INTEREST_OPTIONS.map((option) => (
            <Box
              key={option.title}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                borderRadius: '12px',
                border: '1.5px solid rgba(145, 158, 171, 0.32)',
                backgroundColor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: option.bgColor,
                  fontSize: '24px',
                  flexShrink: 0,
                }}
              >
                {option.emoji}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#212B36' }}>
                  {option.title}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#637381' }}>
                  {option.subtitle}
                </Typography>
              </Box>
              <Iconify icon="mdi:check-circle-outline" sx={{ width: 22, height: 22, color: '#E0E0E0' }} />
            </Box>
          ))}
        </Box>

        {/* Follow-up System Hint */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: '12px',
            bgcolor: 'rgba(255, 171, 0, 0.08)',
            border: '1px solid rgba(255, 171, 0, 0.24)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
          }}
        >
          <Iconify
            icon="mdi:information-outline"
            sx={{ width: 20, height: 20, color: '#FFAB00', flexShrink: 0, mt: 0.25 }}
          />
          <Box>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#B76E00', mb: 0.25 }}>
              Follow-Up System:
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#B76E00', lineHeight: 1.5 }}>
              {followUpText}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Final Outcome Section */}
      <Box>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: '#212B36', mb: 0.5 }}>
          Final Outcome
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#637381', mb: 2 }}>
          Select the call result
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', opacity: 0.7 }}>
          {OUTCOME_OPTIONS.map((option) => (
            <Box
              key={option.title}
              sx={{
                flex: '1 1 auto',
                minWidth: 140,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                p: 2,
                borderRadius: '12px',
                border: '1.5px solid rgba(145, 158, 171, 0.32)',
                backgroundColor: '#fff',
              }}
            >
              <Iconify icon={option.icon} sx={{ width: 28, height: 28, color: option.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#212B36', textAlign: 'center' }}>
                {option.title}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

CloseCall.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  metadata: PropTypes.shape({
    hints: PropTypes.shape({
      followUp: PropTypes.string,
    }),
  }),
};
CloseCall.defaultProps = { label: '', required: false, metadata: null };
